import { VuexModule, Module, Action, Mutation } from "vuex-module-decorators";
import AppointmentApi from "@/api/appointment-api";
import {
  formatDate,
  formatDateCalendar,
  addOpacity,
  getOptimalTextColor,
  deepTrim,
  showRecurrenceDaysOptions,
} from "../../helpers/data";
import {
  installationsModule,
  commonModule,
  companyModule,
} from "@/store/modules/store-accessor";
import _ from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import i18n from "@/i18n";
import { hasAssigneeWithoutName, normalizeAssigneeIdsForSubmit } from "@/helpers/assignees";

dayjs.extend(utc);
dayjs.extend(timezone);

@Module({ namespaced: true, name: "appointments" })
export default class AppointmentsModule extends VuexModule {
  public appointments: AppointmentDto[] = [];
  public calendarAppointments: CalendarAppointmentsTasksDto = {
    appointments: [],
    tasks: [],
  };
  public isLoading = true;
  public isLoadingIndicator = true;
  public filters = `?date_from=${dayjs()
    .set("hour", 0)
    .set("minute", 0)
    .set("second", 0)
    .set("millisecond", 0)
    .toISOString()}&date_to=${dayjs()
    .set("hour", 23)
    .set("minute", 59)
    .set("second", 59)
    .set("millisecond", 999)
    .toISOString()}`; // Default filter for today
  public categories: CategoriesDto = { id: [], name: [] };
  public queryObject: any = {};
  public calendarSelectedType: string;
  public calendarEndDate: any;
  public isLoadingTable = false;
  public appointmentsFilters = ``;
  public search = "";
  public addCalendarInstallation: string | null = null;
  public overlappingEvents: Record<string, any> | null = null;
  public modalCalendarAppointments: Record<string, any> = { appointments: [], tasks: [] };
  public modalCalendarAppointmentsLoader = false;

  @Action({ commit: "setAppointments" })
  async getAppointmentsAction(query: string): Promise<AppointmentDto[]> {
    this.setIsLoadingTable(true);
    const appointmentApi = new AppointmentApi();
    query += `&timezone=${dayjs.tz.guess()}`;
    const appointments = await appointmentApi.getAllAppointments(query);
    this.setIsLoadingTable(false);
    return appointments;
  }

  @Action({ commit: "setCalendarAppointments" })
  async getCalendarAppointmentsAction(
    query: string
  ): Promise<CalendarAppointmentsTasksDto> {
    const appointmentApi = new AppointmentApi();
    this.setIsLoading(true);
    query += `&timezone=${dayjs.tz.guess()}`;
    const calendarAppointments = await appointmentApi.getCalendarAppointments(
      query
    );
    this.setIsLoading(false);
    return calendarAppointments;
  }

  @Action
  async getCalendarAppointmentsExportAction(data: any) {
    const appointmentApi = new AppointmentApi();
    const response = await appointmentApi.getCalendarAppointmentsExport(data);
    const blob = new Blob([response], { type: "application/zip" });
    return blob;
  }

  @Action({ commit: "setModalCalendarAppointments" })
  async getModalCalendarAppointmentsAction(query: string): Promise<Record<string, any>> {
    const appointmentApi = new AppointmentApi();
    this.setModalCalendarAppointmentsLoader(true);
    const modalCalendarAppointments = await appointmentApi.getModalCalendarAppointments(query);
    this.setModalCalendarAppointmentsLoader(false);
    return modalCalendarAppointments;
  }

  @Action
  async createAppointment(data: Record<string, any>) {
    let appointment_data = _.cloneDeep(data);

    appointment_data.assignee_id = appointment_data.assignee_id.id;
    appointment_data.assignee_ids = normalizeAssigneeIdsForSubmit(appointment_data.assignee_ids);
    appointment_data.type_id = appointment_data.type_id.id;
    appointment_data.duration = appointment_data.duration.dur;
    appointment_data.scheduled_start = appointment_data.scheduled_start_time
      ? dayjs(
          appointment_data.scheduled_start +
            " " +
            appointment_data.scheduled_start_time
        ).toISOString()
      : null;
    delete appointment_data.scheduled_start_time;

    if (
      appointment_data.installation_item_id &&
      appointment_data.installation_item_id.id
    ) {
      appointment_data.item_type_text =
        appointment_data.installation_item_id.item_type_text;
      appointment_data.item_make =
        appointment_data.installation_item_id.item_make;
      appointment_data.item_model =
        appointment_data.installation_item_id.item_model;
      appointment_data.item_serial_no =
        appointment_data.installation_item_id.item_serial_no;
      appointment_data.installation_item_id =
        appointment_data.installation_item_id.id;
    } else {
      delete appointment_data.installation_item_id;
    }

    if(appointment_data.is_recurrent) {
      if(showRecurrenceDaysOptions(appointment_data)) {
        appointment_data.days_of_week = appointment_data.days_of_week.map((day: Record<string, string>) => day.value);
      } else {
        delete appointment_data.days_of_week;
      }

      appointment_data.recurrence_type = appointment_data.recurrence_type.value;
      appointment_data.end_type = appointment_data.end_date ? 'until_date' : 'none';
    } else {
      delete appointment_data.days_of_week;
      delete appointment_data.recurrence_type;
      delete appointment_data.interval;
      delete appointment_data.occurrence_count;
    }

    const appointmentApi = new AppointmentApi();

    appointment_data = deepTrim(appointment_data);
    await appointmentApi.createAppointment(appointment_data);

    if (appointment_data.task_id != null) {
      await installationsModule.invalidateInstallationTab({
        tabs: ['appointments', 'tasks', 'items', 'jobs'],
        installationId: appointment_data.installation_id,
      });
      await installationsModule.getInstallationDetailsTasks(appointment_data.installation_id);
    } else {
      await installationsModule.getInstallationDetailsAppointments(appointment_data.installation_id);
      await installationsModule.invalidateInstallationTab({
        tabs: ['tasks', 'items', 'jobs'],
        installationId: appointment_data.installation_id,
      });
    }
    this.setIsLoading(false);
  }

  @Action
  async editAppointment(data: Record<string, any>) {
    let dataPayload = _.cloneDeep(data);
    if (dataPayload.assignee) {
      dataPayload.assignee_id = dataPayload.assignee.id;
    } else {
      delete dataPayload.assignee_id;
    }
    dataPayload.assignee_ids = normalizeAssigneeIdsForSubmit(dataPayload.assignee_ids, dataPayload.assignees);
    if (hasAssigneeWithoutName(dataPayload.assignee_ids)) {
      return;
    }
    dataPayload.type_id = dataPayload.type_id.id;

    if (dataPayload.scheduled_start) {
      dataPayload.scheduled_start = dayjs(
        dataPayload.scheduled_start + " " + dataPayload.scheduled_start_time
      ).toISOString();
      dataPayload.duration = dataPayload.duration.dur;
    } else {
      delete dataPayload.scheduled_start;
      delete dataPayload.duration;
      delete dataPayload.scheduled_start_time;
    }
    if (dataPayload.installation_item_id && dataPayload.installation_item_id.id) {
      dataPayload.item_type_text =
        dataPayload.installation_item_id.item_type_text;
      dataPayload.item_make = dataPayload.installation_item_id.item_make;
      dataPayload.item_model = dataPayload.installation_item_id.item_model;
      dataPayload.item_serial_no =
        dataPayload.installation_item_id.item_serial_no;
      dataPayload.installation_item_id = dataPayload.installation_item_id.id;
    } else {
      delete dataPayload.installation_item_id;
    }
    dataPayload.is_confirmed = dataPayload.appointment_is_confirmed;
    dataPayload.notes = dataPayload.appointment_notes;

    delete dataPayload.appointment_notes;
    delete dataPayload.appointment_is_confirmed;
    delete dataPayload.appointment_status_id;

    const appointmentApi = new AppointmentApi();

    dataPayload = deepTrim(dataPayload);
    await appointmentApi.updateAppointment(dataPayload.id, dataPayload);
    await installationsModule.getInstallationDetailsAppointments(dataPayload.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'items'], installationId: dataPayload.installation_id});
    this.setIsLoading(false);
  }

  @Action
  async deleteAppointment(data: Record<string, any>) {
    const appointmentApi = new AppointmentApi();
    await appointmentApi.deleteAppointment(data.id);
    await installationsModule.getInstallationDetailsAppointments(data.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'items'], installationId: data.installation_id});
    this.setIsLoading(false);
  }

  @Action
  async cancelAppointment(data: Record<string, any>) {
    const appointmentApi = new AppointmentApi();
    data = deepTrim(data);
    await appointmentApi.cancelAppointment(data.id, data);
    await installationsModule.getInstallationDetailsAppointments(data.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'items'], installationId: data.installation_id});
    this.setIsLoading(false);
  }

  @Action
  async endAppointment(data: Record<string, any>) {
    let jobData = _.cloneDeep(data);

    jobData.assignee_id = jobData.assignee_id.id;
    jobData.actions = jobData.type_id;
    jobData.duration = jobData.duration.dur;

    if (jobData.scheduled_start_date && jobData.scheduled_start_time) {
      delete jobData.scheduled_start_date;
      delete jobData.scheduled_start_time;
    }

    if(!jobData.actual_start) {
      delete jobData.actual_start;
    }

    jobData.actions.map((item: Record<string, any>) => {
      item.unit = item.is_time_charged ? item.unit.unit : null;
    });

    if(jobData.materials && jobData.materials.length > 0) {
      jobData.materials.map((item: Record<string, any>) => {
        item.unit = item.is_unit_based ? item.unit : null;
      });
    }

    if(jobData.expenses && jobData.expenses.length > 0) {
      jobData.expenses.map((item: Record<string, any>) => {
        item.expense_label_id = item?.expense_label_id?.id ?? null;
      });
    }

    if (!jobData.is_paid) {
      delete jobData.payments;
    }

    delete jobData.is_paid;
    delete jobData.type_id;

    const appointmentApi = new AppointmentApi();

    jobData = deepTrim(jobData);
    await appointmentApi.endAppointment(jobData.id, jobData);
    await installationsModule.getInstallationDetailsAppointments(jobData.installation_id);
    await installationsModule.invalidateInstallationTab({tabs: ['jobs', 'items', 'photos'], installationId: jobData.installation_id});
    this.setIsLoading(false);
  }

  @Action({ commit: "setCategories" })
  async getCategories() {
    let categories = {};
    if (localStorage.getItem("calendar_type") == "category") {
      categories = {
        name: commonModule.techniciansCombo.map((item: any) => item.name),
        id: commonModule.techniciansCombo.map((item: any) => item.id),
      };
    } else {
      categories = {
        name: commonModule.personnelCombo.map((item: any) => item.name),
        id: commonModule.personnelCombo.map((item: any) => item.id),
      };
    }
    return categories;
  }

  @Action
  async getAppointmentsPdfExportAction({
    query,
    data,
  }: {
    query: string;
    data: Record<string, any>;
  }) {
    const appointmentsApi = new AppointmentApi();
    const response = await appointmentsApi.getAppointmentsPdfExport(
      query,
      data
    );
    const blob = new Blob([response], { type: "application/pdf" });
    return blob;
  }

  @Action({ commit: "setOverlappingEvents" })
  async getOverlappingEvents(data: Record<string, any>): Promise<any[]> {
    const appointmentApi = new AppointmentApi();
    const overlappingEvents = await appointmentApi.getOverlappingEvents(data);

    return overlappingEvents;
  }

  @Action
  async getAppointmentSignature(appointmentId: number) {
    const appointmentApi = new AppointmentApi();
    const response = await appointmentApi.getAppointmentSignature(appointmentId);
    const blob = new Blob([response], { type: "image/*" });
    return blob;
  }

  @Mutation
  public setAppointments(appointments: AppointmentDto[]): void {
    const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;
    
    if (installationItemDescriptionIsEnabled) {
      this.appointments = appointments.map(item => ({
        ...item,
        job_item: item.job_item ? `${item.item_description ?? ''}${item.item_description ? ' | ' : ''}${item.job_item}` : '-'
      }));
    } else {
      this.appointments = appointments;
    }
  }

  @Mutation
  public clearAppointments(): void {
    this.appointments = [];
  }

  @Mutation
  public setCalendarAppointments(
    calendarAppointments: CalendarAppointmentsTasksDto
  ): void {
    const events = [];
    for (let i = 0; i < calendarAppointments.appointments.length; i++) {
      const startDateTimeformatted = formatDateCalendar(
        calendarAppointments.appointments[i].scheduled_start
      );
      const endDateTimeformatted = formatDateCalendar(
        calendarAppointments.appointments[i].scheduled_end
      );

      const startDatetime = new Date(startDateTimeformatted);
      let endDatetime = new Date(endDateTimeformatted);
      const realEndDateTime = new Date(endDateTimeformatted);
      const realStartDateTime = new Date(startDateTimeformatted);

      if (this.calendarSelectedType === "month") {
        if (endDatetime > this.calendarEndDate) {
          endDatetime = this.calendarEndDate;
        }
      } else {
        if (endDatetime.getDate() > startDatetime.getDate()) {
          const nextDayStartDateTime = new Date(realEndDateTime);
          nextDayStartDateTime.setHours(0, 0, 0, 0);

          //split the event to current day and the next day because v-calendar doesnt render it correctly if it spans to multiple days on the Week & Day Calendar. Month works fine
          events.push({
            id: calendarAppointments.appointments[i].id,
            name: calendarAppointments.appointments[i].type_name,
            start: nextDayStartDateTime,
            end: endDatetime,
            color:
              calendarAppointments.appointments[i].appointment_status_id == 4
                ? addOpacity(
                    calendarAppointments.appointments[i].type_color,
                    0.3
                  )
                : calendarAppointments.appointments[i].appointment_status_id ==
                  1
                ? addOpacity(
                    calendarAppointments.appointments[i].type_color,
                    0.5
                  )
                : calendarAppointments.appointments[i].type_color,
            textColor: getOptimalTextColor(
              calendarAppointments.appointments[i].type_color!
            ),
            details:
              ("0" + startDatetime.getHours()).slice(-2) +
              ":" +
              ("0" + startDatetime.getMinutes()).slice(-2) +
              " - " +
              ("0" + realEndDateTime.getHours()).slice(-2) +
              ":" +
              ("0" + realEndDateTime.getMinutes()).slice(-2),
            user: calendarAppointments.appointments[i].assignee_id,
            notes: calendarAppointments.appointments[i].notes,
            address:
              calendarAppointments.appointments[i].address?.split(",")[0],
            client_name: calendarAppointments.appointments[i].client_name,
            tag_id: calendarAppointments.appointments[i].tag_id
              ?.split("_")
              .pop(),
            area: calendarAppointments.appointments[i].area,
            installation_id:
              calendarAppointments.appointments[i].installation_id,
            timed: true,
            assignee_id: calendarAppointments.appointments[i].assignee_id,
            assignee_ids: calendarAppointments.appointments[i].assignee_ids,
            assignees: calendarAppointments.appointments[i].assignees,
            status_id:
              calendarAppointments.appointments[i].appointment_status_id,
            category: calendarAppointments.appointments[i].assignee_id,
            is_business: calendarAppointments.appointments[i].is_business,
            entity_name: calendarAppointments.appointments[i].entity_name,
            installation_item_id:
              calendarAppointments.appointments[i].installation_item_id,
            job_id: calendarAppointments.appointments[i].job_id,
            action_type_id: calendarAppointments.appointments[i].action_type_id,
            appointment_status_id:
              calendarAppointments.appointments[i].appointment_status_id,
            realEndDateTime: realEndDateTime,
            realStartDateTime: realStartDateTime,
            job_item: calendarAppointments.appointments[i].job_item,
            item_type_text: calendarAppointments.appointments[i].item_type_text,
            item_make: calendarAppointments.appointments[i].item_make,
            item_model: calendarAppointments.appointments[i].item_model,
            item_serial_no: calendarAppointments.appointments[i].item_serial_no,
            installation_description: calendarAppointments.appointments[i].installation_description,
            contacts: calendarAppointments.appointments[i].client_contacts,
            actual_start: calendarAppointments.appointments[i].actual_start,
            job_description: calendarAppointments.appointments[i].job_description,
            job_notes: calendarAppointments.appointments[i].job_notes,
            item_description: calendarAppointments.appointments[i].item_description,
            client_email: calendarAppointments.appointments[i].client_email,
            cancellation_reason_id: calendarAppointments.appointments[i].cancellation_reason_id,
            cancellation_text: calendarAppointments.appointments[i].cancellation_text,
            cancellation_reason: calendarAppointments.appointments[i].cancellation_reason,
            is_virtual: calendarAppointments.appointments[i].is_virtual,
            recurrence_id: calendarAppointments.appointments[i].recurrence_id,
            parent_appointment_id: calendarAppointments.appointments[i].appointment_id,
          });

          endDatetime = new Date(startDatetime);
          endDatetime.setHours(23, 59, 59, 999);
        }
      }

      events.push({
        id: calendarAppointments.appointments[i].id,
        name: calendarAppointments.appointments[i].type_name,
        start: startDatetime,
        end: endDatetime,
        color:
          calendarAppointments.appointments[i].appointment_status_id == 4
            ? addOpacity(calendarAppointments.appointments[i].type_color, 0.3)
            : calendarAppointments.appointments[i].appointment_status_id == 1
            ? addOpacity(calendarAppointments.appointments[i].type_color, 0.5)
            : calendarAppointments.appointments[i].type_color,
        textColor: getOptimalTextColor(
          calendarAppointments.appointments[i].type_color!
        ),
        details:
          ("0" + startDatetime.getHours()).slice(-2) +
          ":" +
          ("0" + startDatetime.getMinutes()).slice(-2) +
          " - " +
          ("0" + realEndDateTime.getHours()).slice(-2) +
          ":" +
          ("0" + realEndDateTime.getMinutes()).slice(-2),
        user: calendarAppointments.appointments[i].assignee_id,
        notes: calendarAppointments.appointments[i].notes,
        address: calendarAppointments.appointments[i].address?.split(",")[0],
        client_name: calendarAppointments.appointments[i].client_name,
        tag_id: calendarAppointments.appointments[i].tag_id?.split("_").pop(),
        area: calendarAppointments.appointments[i].area,
        installation_id: calendarAppointments.appointments[i].installation_id,
        timed: true,
        assignee_id: calendarAppointments.appointments[i].assignee_id,
        assignee_ids: calendarAppointments.appointments[i].assignee_ids,
        assignees: calendarAppointments.appointments[i].assignees,
        status_id: calendarAppointments.appointments[i].appointment_status_id,
        category: calendarAppointments.appointments[i].assignee_id,
        is_business: calendarAppointments.appointments[i].is_business,
        entity_name: calendarAppointments.appointments[i].entity_name,
        installation_item_id:
          calendarAppointments.appointments[i].installation_item_id,
        job_id: calendarAppointments.appointments[i].job_id,
        realEndDateTime: realEndDateTime,
        realStartDateTime: realStartDateTime,
        job_description: calendarAppointments.appointments[i].job_description,
        job_item: calendarAppointments.appointments[i].job_item,
        item_type_text: calendarAppointments.appointments[i].item_type_text,
        item_make: calendarAppointments.appointments[i].item_make,
        item_model: calendarAppointments.appointments[i].item_model,
        item_serial_no: calendarAppointments.appointments[i].item_serial_no,
        action_type_id: calendarAppointments.appointments[i].action_type_id,
        appointment_status_id:
          calendarAppointments.appointments[i].appointment_status_id,
        installation_description: calendarAppointments.appointments[i].installation_description,
        contacts: calendarAppointments.appointments[i].client_contacts,
        actual_start: calendarAppointments.appointments[i].actual_start,
        item_description: calendarAppointments.appointments[i].item_description,
        job_notes: calendarAppointments.appointments[i].job_notes,
        client_email: calendarAppointments.appointments[i].client_email,
        cancellation_reason_id: calendarAppointments.appointments[i].cancellation_reason_id,
        cancellation_text: calendarAppointments.appointments[i].cancellation_text,
        cancellation_reason: calendarAppointments.appointments[i].cancellation_reason,
        is_virtual: calendarAppointments.appointments[i].is_virtual,
        recurrence_id: calendarAppointments.appointments[i].recurrence_id,
        parent_appointment_id: calendarAppointments.appointments[i].appointment_id,
      });
    }

    this.calendarAppointments.appointments = events;

    // Task events

    const taskEvents = [];
    for (let i = 0; i < calendarAppointments.tasks.length; i++) {
      const companyTimezone = companyModule.company?.timezone || dayjs.tz.guess();
      const dueAt = calendarAppointments.tasks[i].due_at;
      const dueAtInCompanyTz = calendarAppointments.tasks[i].all_day
        ? dayjs.tz(dueAt, companyTimezone)
        : dayjs(dueAt);

      taskEvents.push({
        id: calendarAppointments.tasks[i].id,
        name:
          calendarAppointments.tasks[i].category &&
          calendarAppointments.tasks[i].category[0] === 1
            ? i18n.t("reminder_label")
            : calendarAppointments.tasks[i].title,
        start: calendarAppointments.tasks[i].all_day
          ? dueAtInCompanyTz.startOf("day").toDate()
          : new Date(dueAt),
        end: calendarAppointments.tasks[i].all_day
          ? dueAtInCompanyTz.endOf("day").toDate()
          : new Date(new Date(dueAt).setMinutes(new Date(dueAt).getMinutes() + 30)),

        color: [3, 4, 5].includes(calendarAppointments.tasks[i].task_status_id)
          ? addOpacity("#2da1ff", 0.5)
          : "#2da1ff",

        details:
          ("0" + new Date(dueAt).getHours()).slice(-2) +
          ":" +
          ("0" + new Date(dueAt).getMinutes()).slice(-2),
        user: calendarAppointments.tasks[i].assignee_id,
        notes: calendarAppointments.tasks[i].notes,
        address: calendarAppointments.tasks[i].address?.split(",")[0],
        client_name: calendarAppointments.tasks[i].name,
        tag_id: calendarAppointments.tasks[i].tag_id?.split("_").pop(),
        area: calendarAppointments.tasks[i].area,
        installation_id: calendarAppointments.tasks[i].installation_id,
        timed: !calendarAppointments.tasks[i].all_day,
        assignee_id: calendarAppointments.tasks[i].assignee_id,
        assignee_ids: calendarAppointments.tasks[i].assignee_ids,
        assignees: calendarAppointments.tasks[i].assignees,
        category: calendarAppointments.tasks[i].assignee_id,
        isTask: true,
        all_day: calendarAppointments.tasks[i].all_day,
        action_type_id: calendarAppointments.tasks[i].action_type_id,
        due_at: calendarAppointments.tasks[i].due_at,
        task_status_id: calendarAppointments.tasks[i].task_status_id,
        installation_item_id:
          calendarAppointments.tasks[i].task_installation_item_id,
        is_orphan: calendarAppointments.tasks[i].installation_id ? false : true,
        task_category: calendarAppointments.tasks[i].category,
        task_item: calendarAppointments.tasks[i].task_item,
        item_type_text: calendarAppointments.tasks[i].item_type_text,
        item_make: calendarAppointments.tasks[i].item_make,
        item_model: calendarAppointments.tasks[i].item_model,
        item_serial_no: calendarAppointments.tasks[i].item_serial_no,
        installation_description: calendarAppointments.tasks[i].installation_description,
        contacts: calendarAppointments.tasks[i].client_contacts,
        item_description: calendarAppointments.tasks[i].item_description,
        priority_name: calendarAppointments.tasks[i].priority_name,
        priority_level: calendarAppointments.tasks[i].priority_level,
        priority_color: calendarAppointments.tasks[i].priority_color,
      });
    }

    (this.calendarAppointments as any).tasks = taskEvents;

    this.calendarAppointments.appointments =
      this.calendarAppointments.appointments.concat(
        this.calendarAppointments.tasks as any
      );
  }

  @Mutation
  public clearCalendarAppointments(): void {
    this.calendarAppointments.appointments = [];
  }

  @Mutation
  public setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @Mutation
  public setFilters(filters: string): void {
    this.filters = filters;
  }

  @Mutation
  public setCategories(): void {
    const params = new URLSearchParams(this.filters);
    const assigneeIds = params.getAll("assignee_id[]");

    let techniciansCombo = commonModule.techniciansCombo;
    let personnelCombo = commonModule.personnelCombo;

    if (assigneeIds.length > 0) {
      techniciansCombo = techniciansCombo.filter(
        (assignee: Record<string, any>) => assigneeIds.includes(assignee.id)
      );
      personnelCombo = personnelCombo.filter((assignee: Record<string, any>) =>
        assigneeIds.includes(assignee.id)
      );
    }

    if (localStorage.getItem("calendar_type") == "category") {
      this.categories = {
        name: techniciansCombo.map((item: any) => item.name),
        id: techniciansCombo.map((item: any) => item.id),
      };
    } else {
      this.categories = {
        name: personnelCombo.map((item: any) => item.name),
        id: personnelCombo.map((item: any) => item.id),
      };
    }
  }

  @Mutation
  public setIsLoadingIndicator(isLoadingIndicator: boolean): void {
    this.isLoadingIndicator = isLoadingIndicator;
  }

  @Mutation
  public setQueryObject(queryObject: any): void {
    this.queryObject = queryObject;
  }

  @Mutation
  public setCalendarSelectedType(calendarSelectedType: string): void {
    this.calendarSelectedType = calendarSelectedType;
  }

  @Mutation
  public setCalendarEndDate(calendarEndDate: any): void {
    this.calendarEndDate = calendarEndDate;
    this.calendarEndDate = new Date(calendarEndDate);
    this.calendarEndDate.setHours(23, 59, 59, 999);
  }

  @Mutation
  public setIsLoadingTable(isLoadingTable: boolean): void {
    this.isLoadingTable = isLoadingTable;
  }

  @Mutation
  public setAppointmentsFilter(appointmentsFilters: any): void {
    this.appointmentsFilters = appointmentsFilters;
  }

  @Mutation
  public setSearch(search: string): void {
    this.search = search;
  }

  @Mutation
  public unsetCalendarAppointments(): void {
    this.calendarAppointments.appointments = [];
  }

  @Mutation
  public setAddCalendarInstallation(installationId: string): void {
    this.addCalendarInstallation = installationId;
  }

  @Mutation
  public setOverlappingEvents(overlappingEvents: Record<string, any> | null): void {
    if(overlappingEvents) {
      this.overlappingEvents = overlappingEvents;
    } else {
      this.overlappingEvents = null;
    }
  }

  @Mutation
  public setModalCalendarAppointments(modalCalendarAppointments: Record<string, any>): void {
    const events = [];
    for (let i = 0; i < modalCalendarAppointments.appointments.length; i++) {
      const raw = modalCalendarAppointments.appointments[i] as Record<string, any>;
      const startDateTimeformatted = formatDateCalendar(raw.scheduled_start);
      const endDateTimeformatted = formatDateCalendar(raw.scheduled_end);

      const startDatetime = new Date(startDateTimeformatted);
      let endDatetime = new Date(endDateTimeformatted);
      const realEndDateTime = new Date(endDateTimeformatted);
      const realStartDateTime = new Date(startDateTimeformatted);

      if (endDatetime.getDate() > startDatetime.getDate()) {
        const nextDayStartDateTime = new Date(realEndDateTime);
        nextDayStartDateTime.setHours(0, 0, 0, 0);

        endDatetime = new Date(startDatetime);
        endDatetime.setHours(23, 59, 59, 999);
      }

      events.push({
        start: startDatetime,
        end: endDatetime,
        event_id: raw.id,
        name: raw.type_name,
        type_name: raw.type_name,
        color: raw.appointment_status_id == 4
        ? addOpacity(raw.type_color, 0.3)
        : raw.appointment_status_id == 1
        ? addOpacity(raw.type_color, 0.5)
        : raw.type_color,
        textColor: getOptimalTextColor(raw.type_color!),
        details: ("0" + startDatetime.getHours()).slice(-2) + ":" + ("0" + startDatetime.getMinutes()).slice(-2) +
        " - " + ("0" + realEndDateTime.getHours()).slice(-2) + ":" + ("0" + realEndDateTime.getMinutes()).slice(-2),
        user: raw.assignee_id,
        notes: raw.notes,
        address: raw.address?.split(",")[0],
        area: raw.area,
        timed: true,
        client_name: raw.client_name,
        assignee_id: raw.assignee_id,
        assignee_ids: raw.assignee_ids,
        assignees: raw.assignees,
        is_business: raw.is_business,
        entity_name: raw.entity_name,
        realEndDateTime: realEndDateTime,
        realStartDateTime: realStartDateTime,
        action_type_id: raw.action_type_id,
        appointment_status_id: raw.appointment_status_id,
        status_id: raw.appointment_status_id,
        actual_start: raw.actual_start,
        recurrence_id: raw.recurrence_id,
      });
    }

    this.modalCalendarAppointments.appointments = events;

    // Task events

    const taskEvents = [];
    for (let i = 0; i < modalCalendarAppointments.tasks.length; i++) {
      const companyTimezone = companyModule.company?.timezone || dayjs.tz.guess();
      const dueAt = modalCalendarAppointments.tasks[i].due_at;
      const dueAtInCompanyTz = modalCalendarAppointments.tasks[i].all_day
        ? dayjs.tz(dueAt, companyTimezone)
        : dayjs(dueAt);

      taskEvents.push({
        id: modalCalendarAppointments.tasks[i].id,
        name: modalCalendarAppointments.tasks[i].category && modalCalendarAppointments.tasks[i].category[0] === 1 ? i18n.t("reminder_label") : modalCalendarAppointments.tasks[i].title,
        start: modalCalendarAppointments.tasks[i].all_day
          ? dueAtInCompanyTz.startOf("day").toDate()
          : new Date(dueAt),
        end: modalCalendarAppointments.tasks[i].all_day
          ? dueAtInCompanyTz.endOf("day").toDate()
          : new Date(new Date(dueAt).setMinutes(new Date(dueAt).getMinutes() + 30)),
        color: [3, 4, 5].includes(modalCalendarAppointments.tasks[i].task_status_id) ? addOpacity("#2da1ff", 0.5) : "#2da1ff",
        details: ("0" + new Date(dueAt).getHours()).slice(-2) + ":" + ("0" + new Date(dueAt).getMinutes()).slice(-2),
        user: modalCalendarAppointments.tasks[i].assignee_id,
        timed: !modalCalendarAppointments.tasks[i].all_day,
        area: modalCalendarAppointments.tasks[i].area,
        client_name: modalCalendarAppointments.tasks[i].name,
        assignee_id: modalCalendarAppointments.tasks[i].assignee_id,
        assignee_ids: modalCalendarAppointments.tasks[i].assignee_ids,
        assignees: modalCalendarAppointments.tasks[i].assignees,
        category: modalCalendarAppointments.tasks[i].assignee_id,
        isTask: true,
        all_day: modalCalendarAppointments.tasks[i].all_day,
        action_type_id: modalCalendarAppointments.tasks[i].action_type_id,
        due_at: modalCalendarAppointments.tasks[i].due_at,
        task_status_id: modalCalendarAppointments.tasks[i].task_status_id,
        priority_name: modalCalendarAppointments.tasks[i].priority_name,
        priority_level: modalCalendarAppointments.tasks[i].priority_level,
        priority_color: modalCalendarAppointments.tasks[i].priority_color,
      });
    }

    (this.modalCalendarAppointments as any).tasks = taskEvents;

    this.modalCalendarAppointments.appointments = this.modalCalendarAppointments.appointments.concat(this.modalCalendarAppointments.tasks as any);
  }

  @Mutation
  public unsetModalCalendarAppointments(): void {
    this.modalCalendarAppointments.appointments = [];
    this.modalCalendarAppointments.tasks = [];
    this.overlappingEvents = {};
  }

  @Mutation
  public setModalCalendarAppointmentsLoader(modalCalendarAppointmentsLoader: boolean) {
    this.modalCalendarAppointmentsLoader = modalCalendarAppointmentsLoader;
  }

}
